import { useTranslation } from 'react-i18next'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { RemoteContent, Title, formatDate, formatHours, formatRange } from '@jupi/ui'
import { useMaintenanceStatus, type MaintenanceStatus } from '../api/queries'

// huoltostatus.php, as cards rather than an eight-column table. The table
// was already unreadable on a phone, and this is a page pilots check from
// the hangar.
export default function MaintenancePage() {
  const { t } = useTranslation()
  const { data: status = [], isLoading, error } = useMaintenanceStatus()

  return (
    <Box>
      <Title label={t('maintenance.title')} />

      <RemoteContent isLoading={isLoading} error={error}>
        <Stack spacing={2}>
          {status.map((row) => (
            <AircraftMaintenanceCard key={row.registration} row={row} />
          ))}
        </Stack>
      </RemoteContent>

      {/* The legacy page carried this warning at length, and it matters:
          these are estimates from pilots' own guesses, not the logbook. */}
      <Alert severity="info" sx={{ mt: 3 }}>
        {t('maintenance.disclaimer')}
      </Alert>
    </Box>
  )
}

function AircraftMaintenanceCard({ row }: { row: MaintenanceStatus }) {
  const { t } = useTranslation()
  const overdue = row.hoursRemaining !== null && row.hoursRemaining < 0

  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', mb: 1.5 }}>
          <Typography variant="h6">{row.registration}</Typography>
          <Typography variant="body2" color="text.secondary">
            {row.aircraftType}
          </Typography>
          <Box sx={{ flex: 1 }} />
          {row.nextMaintenanceDescription ? (
            <Chip size="small" label={row.nextMaintenanceDescription} />
          ) : null}
        </Stack>

        <Stack
          direction="row"
          sx={{ flexWrap: 'wrap', gap: 2 }}
          divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />}
        >
          <Figure label={t('maintenance.hoursAtBaseline')} value={formatHours(row.hoursAtBaseline)} />
          <Figure label={t('maintenance.hoursNow')} value={formatHours(row.hoursNow)} />
          <Figure
            label={t('maintenance.nextMaintenanceHours')}
            value={row.nextMaintenanceHours === null ? '–' : formatHours(row.nextMaintenanceHours)}
          />
          <Figure
            label={t('maintenance.hoursRemaining')}
            value={row.hoursRemaining === null ? '–' : formatHours(row.hoursRemaining)}
            // Green when there is time left, red when overdue -- the same
            // signal huoltostatus.php gave with #009e38 and red.
            color={row.hoursRemaining === null ? undefined : overdue ? 'error.main' : 'success.main'}
          />
          <Figure label={t('maintenance.prediction')} value={outcomeLabel(row, t)} />
        </Stack>

        {row.maintWindowStart && row.maintWindowEnd ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t('maintenance.window')}: {formatRange(row.maintWindowStart, row.maintWindowEnd)}
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  )
}

function Figure({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <Box sx={{ minWidth: 120 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 600, color }}>
        {value}
      </Typography>
    </Box>
  )
}

function outcomeLabel(row: MaintenanceStatus, t: (key: string) => string): string {
  switch (row.outcome.kind) {
    case 'date':
      return formatDate(`${row.outcome.date}T12:00:00Z`)
    case 'exceeded':
      return t('maintenance.exceeded')
    default:
      // "No estimate yet" is a real answer: the aircraft is simply not
      // booked far enough ahead to reach the threshold.
      return t('maintenance.noEstimateYet')
  }
}
